import * as et from '../../../application/EventTypes';
import * as dte from "../../DtEventTypes";
import debounce from "../../streams/timed-debounce";
import { FacetsScope } from '../../facets/Facets';

import { AssetsLayer } from './assets/AssetsLayer';
import { LabelCompositor, PRIORITIES } from "../LabelCompositor";
import { LevelsLayer } from './levels/LevelsLayer';
import { SpacesLayer } from './spaces/SpacesLayer';
import { TraceLayer_DEPRECATED } from "./trace/TraceLayer";
import { createDomLayer, HUD_LAYER } from "./HudLayer";
import { setCanvas2DFrom3DPosition } from '../ui/positions';

const avp = Autodesk.Viewing.Private;

export class HudLayers {
  #hud;
  #facility;
  #viewer;
  #prefsView;

  layers;

  constructor(viewer, facility, hud) {
    this.#hud = hud;
    this.#viewer = viewer;
    this.#facility = facility;
    this.#prefsView = {};
    this.dom = createDomLayer(hud.getDocument(), 'dt-hud-layers');

    this.layers = [
    new AssetsLayer(facility, hud, viewer),
    new LevelsLayer(facility, hud, viewer),
    new SpacesLayer(facility, hud, viewer)];

    if (!facility.loadContext.dynamicLabels) {
      this.layers.unshift(new TraceLayer_DEPRECATED(facility, hud, viewer));
    }
    this.layers.forEach((l) => {this.dom.appendChild(l.dom);});
    this.compositor = new LabelCompositor(this.#viewer);

    this.boundOnSelectionChanged = this.#onSelectionChanged.bind(this);
    this.boundOnIsolationChanged = this.#onIsolationChanged.bind(this);
    this.boundOnFacetsUpdated = this.#onFacetsUpdated.bind(this);
    this.boundOnFacetsLoaded = this.#onFacetsLoaded.bind(this);
    this.boundOnHiddenChanged = this.#onHiddenChanged.bind(this);
    this.boundOnModelChanged = this.#onModelChanged.bind(this);
    this.boundCameraChangedEvent = this.#onCameraChanged.bind(this);
    this.boundViewerResizeEvent = this.#onViewerResize.bind(this);
    this.boundOnViewChanged = this.#onViewChanged.bind(this);

    // Temporary
    this.boundOnClusterTransition = this.#onClusterTransition.bind(this);
    this.updateAllUIPos = debounce(this.#updateAllUIPosImpl.bind(this), 0, { leading: false });
  }

  dispose() {
    this.#viewer.removeEventListener(et.AGGREGATE_SELECTION_CHANGED_EVENT, this.boundOnSelectionChanged);
    this.#viewer.removeEventListener(et.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
    this.#viewer.removeEventListener(et.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnHiddenChanged);
    this.#viewer.removeEventListener(et.CAMERA_CHANGE_EVENT, this.boundCameraChangedEvent);
    this.#viewer.removeEventListener(et.VIEWER_RESIZE_EVENT, this.boundViewerResizeEvent);
    this.#facility.eventTarget.removeEventListener(dte.DT_FACETS_UPDATED, this.boundOnFacetsUpdated);
    this.#facility.eventTarget.removeEventListener(dte.DT_FACETS_LOADED, this.boundOnFacetsLoaded);
    this.#facility.eventTarget.removeEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
    this.#facility.eventTarget.views.removeEventListener(dte.DT_CURRENT_VIEW_CHANGED_EVENT, this.boundOnViewChanged);

    // TEMPORARY hide layers while clustering.
    this.#viewer.removeEventListener(et.TRANSITION_STARTED, this.boundOnClusterTransition);
    this.#viewer.removeEventListener(et.TRANSITION_ENDED, this.boundOnClusterTransition);

    this.layers.forEach((l) => l.dispose());

    this.#prefsView = {};
  }

  init(viewer) {
    this.#viewer = viewer;
    this.#viewer.addEventListener(et.AGGREGATE_SELECTION_CHANGED_EVENT, this.boundOnSelectionChanged);
    this.#viewer.addEventListener(et.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
    this.#viewer.addEventListener(et.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnHiddenChanged);
    this.#viewer.addEventListener(et.CAMERA_CHANGE_EVENT, this.boundCameraChangedEvent);
    this.#viewer.addEventListener(et.VIEWER_RESIZE_EVENT, this.boundViewerResizeEvent);
    this.#facility.eventTarget.addEventListener(dte.DT_FACETS_UPDATED, this.boundOnFacetsUpdated);
    this.#facility.eventTarget.addEventListener(dte.DT_FACETS_LOADED, this.boundOnFacetsLoaded);
    this.#facility.eventTarget.addEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
    this.#facility.eventTarget.views.addEventListener(dte.DT_CURRENT_VIEW_CHANGED_EVENT, this.boundOnViewChanged);

    // TEMPORARY hide layers while clustering.
    this.#viewer.addEventListener(et.TRANSITION_STARTED, this.boundOnClusterTransition);
    this.#viewer.addEventListener(et.TRANSITION_ENDED, this.boundOnClusterTransition);

    this.layers.forEach((l) => l.init());

    return this;
  }

  /** Force invalidate all layers */
  invalidate() {
    this.layers.forEach((l) => l.invalidate());
  }

  getLayer(id) {
    for (const l of this.layers)
    if (l.layerID === id)
    return l;

    return null;
  }

  /** Compositing labels. Sets label visibility based on ongoing composition and priority from update. */
  compose() {
    if (!this.compositor.isLoaded) return;

    this.compositor.startFrame();
    // Since cross-layer priority compositing would require a priority system all layers agree on, we use a cruder
    // solution asking each layer in order, by priority-level.
    for (const priority of PRIORITIES) {
      for (const layer of this.layers) {
        if (layer.isVisible()) {
          layer.compose(this.compositor, priority);
        }
      }
    }
    this.compositor.endFrame();

    // Redraw request can be non-layer specific, for example the asset layer might ask for a redraw for the
    // benefit of deferred labels in other layers.
    for (const layer of this.layers) {
      layer.needsRedraw = false;
    }
  }

  /** Allows layer to update their state, curate labels, and assign them priority. */
  update(ts) {
    let wantRedraw = false;
    for (const layer of this.layers) {
      if (layer.isDirty) {
        wantRedraw = layer.update(ts) || wantRedraw;
      }

      // This is specific to the AssetLayer, because asset details are aggregated in the compose step
      // and since some changes don't require a full update (ex: connection state),
      // we need a way to force a redraw outside an update.
      wantRedraw ||= layer.needsRedraw;
    }

    return wantRedraw;
  }

  animateAllUIs(lerp) {
    for (const layer of this.layers) {
      if (!layer.isVisible()) continue;

      for (const ui of layer.uis()) {
        // Animating along a line is cheap, update while hidden to have for bounds.
        ui.position.animate?.(lerp);
      }
    }
  }

  // Update all UI canvas positions based on the camera position, lerp [0, 1] indicates a transition in progress.
  #updateAllUIPosImpl(layers = this.layers) {
    const camera = this.#viewer.getCamera();
    const transform = camera.projectionMatrix.clone();
    transform.multiply(camera.matrixWorldInverse);

    const w2c = (pos3D, dstPos2D) => {
      setCanvas2DFrom3DPosition(pos3D, dstPos2D, camera, transform);
    };

    for (const layer of layers) {
      if (!layer.isVisible()) continue;

      for (const ui of layer.uis()) {
        // For now, not skipping onCameraUpdate as the cached position is used for the collision target.
        // if (ui.isVisible())
        ui.position.onCameraUpdate?.(w2c);
      }
    }
  }

  #onClusterTransition({ clusterSetLayout, type, sceneAnimState, useGPUAnimation }) {
    const isClustered = !!this.#facility.facetsManager.currentClusterFacet;
    const trackingLayers = [];
    for (const layer of this.layers) {
      const prefs = this.getLayerPreference(layer.layerID);
      layer.onClusterTransition(type, prefs, isClustered);

      if (layer.isVisible() && layer.hasTrackingLabels) {
        trackingLayers.push(layer);
      }
    }

    // Slight optimization to skip if unnecessary.
    if (!trackingLayers.length) return;

    // Wait until the next frame for elements to know their animation matrix.
    requestAnimationFrame(() => {
      if (type === et.TRANSITION_STARTED) {
        for (const layer of trackingLayers) {
          for (const ui of layer.uis()) {
            ui.position.preTransition?.(clusterSetLayout, sceneAnimState, useGPUAnimation);
          }
        }

        avp.fadeValue(
          1e-10 /* Starts slightly after 0 to understand > 0 as a transition. */,
          1.0,
          2.0 /* see anim duration in clustering.js */,
          (t) => {
            this.animateAllUIs(avp.smootherStep(t));
            this.updateAllUIPos(trackingLayers);
          });

      }if (type === et.TRANSITION_ENDED) {
        for (const layer of trackingLayers) {
          for (const ui of layer.uis()) {
            ui.position.postTransition?.();
          }
        }
      }
    });
  }

  #onSelectionChanged({ selections }) {
    this.layers.forEach((l) => l.onSelectionChanged(selections));
  }

  #onIsolationChanged({ isolation }) {
    this.layers.forEach((l) => l.onIsolationChanged(isolation));
  }

  #onHiddenChanged({ hidden }) {
    this.layers.forEach((l) => l.onHiddenChanged(hidden));
  }

  #onFacetsUpdated(event) {
    this.layers.forEach((l) => l.onFacetsUpdated(event));

    this.compositor.updateNextRollupConfig(
      this.#facility.facetsManager._modelOnlyFilter(FacetsScope.Context),
      this.getLayer(HUD_LAYER.LEVELS.id).getTargets(),
      this.getLayer(HUD_LAYER.SPACES.id).getTargets()
    );
  }

  #onFacetsLoaded(event) {
    this.layers.forEach((l) => l.onFacetsLoaded(event));
  }

  #onModelChanged(event) {
    this.layers.forEach((l) => l.onModelChanged(event));
  }

  #onCameraChanged({ camera }) {
    this.#hud.repaint();

    this.layers.forEach((l) => {
      if (l.isVisible()) l.onCameraChanged(camera);
    });

    this.updateAllUIPos();
    this.updateAllUIPos.flush();
  }

  #onViewChanged({ detail: { view } }) {
    this.#prefsView = view?.hud?.layers || {};

    const isClustered = !!view?.facets.clusteredFacetId;
    for (const layer of this.layers) {
      const prefs = this.getLayerPreference(layer.layerID);
      layer.setState(prefs);
      layer.onClusterTransition(et.TRANSITION_ENDED, prefs, isClustered);
    }
  }

  #onViewerResize({ width, height }) {
    this.#hud.resize(width, height);

    if (!this.compositor.isLoaded) return;

    this.compositor.resize({ width, height });
  }

  getLayerPreference(layerID) {
    if (!this.#facility.loadContext.dynamicLabels && layerID === HUD_LAYER.ASSETS.id) {
      return { enabled: false };
    }

    return this.#prefsView[layerID] || HUD_LAYER[layerID].defaultPrefs;
  }

  getStateForView() {
    return this.layers.reduce((res, l) => {
      res[l.layerID] = l.getState();
      return res;
    }, {});
  }
}